<template>
  <div class="modal-overlay z2500 wn-overlay" @click.self="$emit('close')">
    <div class="wn" role="dialog" aria-modal="true" aria-label="What's new">
      <button class="modal-close" @click="$emit('close')" aria-label="Close">✕</button>
      <div class="wn-title">✨ Welcome to Wiznaib.us</div>
      <div class="wn-sub">Luxor's companion site for FFTBG — everything the bracket doesn't tell you:</div>

      <div v-if="!known" class="wn-login">
        <div class="wn-login-text"><b>Start with who you are.</b> Log in with Twitch and the site highlights your units on the bracket,
          puts your profile one click away and tracks your goals. It only learns your name.</div>
        <button class="wn-login-btn" @click="login">Log in with Twitch</button>
      </div>

      <div class="wn-grid">
        <component :is="f.href ? 'a' : 'div'" v-for="f in features" :key="f.title" class="wn-feat" :class="{ link: f.href }" :href="f.href || null" @click="f.href && markOnboardSeen('intro')">
          <span class="wn-feat-emoji">{{ f.emoji }}</span>
          <div class="wn-feat-text">
            <div class="wn-feat-title">{{ f.title }}<span v-if="f.href" class="wn-go"> →</span></div>
            <div class="wn-feat-body">{{ f.body }}</div>
          </div>
        </component>
      </div>

      <div class="wn-foot">
        <button class="wn-tour" @click="$emit('tour')">Take the tour →</button>
        <button class="wn-got" @click="$emit('close')">Got it, let me explore</button>
      </div>
    </div>
  </div>
</template>

<script>
import modalA11y from '../mixins/modalA11y.js';
import { startTwitchLogin } from '../lib/twitchAuth.js';
import { loadPrefs } from '../lib/userPrefs.js';
import { markOnboardSeen } from '../lib/onboarding.js';

export default {
  name: 'WhatsNewModal',
  mixins: [modalA11y],
  emits: ['close', 'tour'],
  data() {
    return {
      known: !!(loadPrefs().myUsername || '').trim(),   // already told us who they are: skip the login pitch
      // what the site has, as of the 2026-10 refresh; tiles for other sections link there (this page's own have no link)
      features: [
        { emoji: '⚔️', title: 'The fight on now', body: 'The live match with both teams’ grades and who’s favored. Click a unit to see what it does to the other team; the bracket and every team’s full stats sit below.' },
        { emoji: '🧪', title: 'Battle lab', body: 'Simulate any match on its map, watch one battle play out turn by turn (and what each unit was thinking), and see the map in 3D.' },
        { emoji: '👤', title: 'Players & profiles', body: '17k players: career stats, championships, every unit fielded, every bet placed. Pin a goal and track it on this page.', href: '?players' },
        { emoji: '🎖️', title: 'Quests', body: 'Monster, Everything, Miku, VIP, Gift and more: who’s done what, where you stand, and this week’s finishers.', href: '?quests' },
        { emoji: '📜', title: 'History', body: 'The Tournament of Champions, every title reign in order, season by season, and the best and worst teams ever.', href: '?champions' },
        { emoji: '🎮', title: 'Game reference', body: 'Every class, ability and item, all 138 maps in 3D, and win rates by class, skill and gear.', href: '?classes' },
        { emoji: '📚', title: 'Learn', body: 'The guide and every chat command, how battles work, how the AI picks its moves, and the questions chat asks most.', href: '?guide' },
        { emoji: '💬', title: 'Community', body: 'The daily paper (yesterday’s chat as a tabloid), copypasta, gifts, and six years of chat lore.', href: '?paper' },
      ],
    };
  },
  methods: {
    // Twitch brings them back to this page; mark the welcome seen so it doesn't greet them twice.
    login() { markOnboardSeen('intro'); startTwitchLogin(); },
    markOnboardSeen,   // a tile link leaves the page: the welcome still counts as seen
  },
};
</script>

<style scoped>
.wn-overlay { background: rgba(0, 0, 0, 0.72); font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; }
.wn { position: relative; width: 100%; max-width: 680px; max-height: 88vh; overflow-y: auto; background: linear-gradient(180deg, var(--panel-top), var(--panel-bottom)); border: 1px solid var(--brass-edge); border-radius: 6px; padding: 22px 26px 20px; color: var(--ink-hi); box-shadow: 0 14px 50px rgba(0, 0, 0, 0.6); text-align: left; }
.wn-title { font-size: 22px; font-family: var(--display); font-weight: 700; color: var(--brass-hi); }
.wn-sub { font-size: 13px; opacity: 0.8; margin: 4px 0 16px; }
.wn-login { display: flex; flex-wrap: wrap; align-items: center; gap: 10px 16px; margin: -4px 0 16px; padding: 12px 14px; background: rgba(100, 65, 165, 0.22); border: 1px solid #7d5bbe; border-radius: 8px; }
.wn-login-text { flex: 1 1 260px; font-size: 13px; line-height: 1.45; }
.wn-login-btn { background: #6441a5; color: #fff; border: 1px solid #a68be0; border-radius: 6px; padding: 8px 14px; font-size: 14px; font-weight: bold; cursor: pointer; white-space: nowrap; }
.wn-login-btn:hover { background: #7d5bbe; }

.wn-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.wn-feat { display: flex; gap: 10px; background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 8px; padding: 10px 12px; }
a.wn-feat { color: inherit; text-decoration: none; }
.wn-feat.link:hover, .wn-feat.link:focus-visible { border-color: var(--brass-edge); background: rgba(217, 180, 94, 0.08); outline: none; }
.wn-go { color: var(--brass); font-weight: normal; }
.wn-feat-emoji { font-size: 22px; line-height: 1.2; flex: 0 0 auto; }
.wn-feat-title { font-size: 13px; font-weight: bold; }
.wn-feat-body { font-size: 11.5px; opacity: 0.78; line-height: 1.4; margin-top: 2px; }

.wn-foot { display: flex; gap: 10px; justify-content: flex-end; margin-top: 18px; }
.wn-foot button { font-size: 13px; padding: 8px 16px; border-radius: 7px; cursor: pointer; }
.wn-tour { background: #c79215; border: 1px solid var(--brass-hi); color: #fff; font-weight: bold; }
.wn-tour:hover { background: #e0a91c; }
.wn-got { background: rgba(18, 24, 38, 0.7); border: 1px solid var(--brass-edge); color: var(--ink); }
.wn-got:hover { background: rgba(217, 180, 94, 0.6); color: #fff; }

@media (max-width: 560px) { .wn-grid { grid-template-columns: 1fr; } }
</style>
